import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Page } from '../../components/layout/AppShell'
import { PageHeader } from '../../components/layout/PageHeader'
import { Badge, Panel, Spinner, ErrorState } from '../../components/ui'
import { fmtDateTime } from '../../lib/format'
import { useScriptRun } from '../trades/useTrades'

export function ScriptRunDetailPage() {
  const { thesisId, scriptRunId } = useParams({ from: '/app/theses/$thesisId/script-runs/$scriptRunId' })
  const navigate = useNavigate()
  const { data: run, isLoading, isError } = useScriptRun(scriptRunId)

  const crumbs = [
    { label: 'Theses', onClick: () => navigate({ to: '/theses' }) },
    { label: `Thesis ${thesisId.slice(0, 8)}`, onClick: () => navigate({ to: '/theses/$thesisId', params: { thesisId } }) },
    { label: `Script run ${scriptRunId.slice(0, 8)}` },
  ]

  if (isLoading) {
    return <Page width="wide" header={<PageHeader crumbs={crumbs} />}><Spinner /></Page>
  }
  if (isError || !run) {
    return <Page width="wide" header={<PageHeader crumbs={crumbs} />}><ErrorState message="Couldn't load this script run." /></Page>
  }

  const exitTone = run.exit_code === 0 ? 'green' : run.exit_code == null ? 'neutral' : 'red'

  return (
    <Page
      width="wide"
      header={
        <PageHeader
          crumbs={crumbs}
          actions={<Badge tone={exitTone}>exit {run.exit_code ?? '—'}</Badge>}
        />
      }
    >
      <Panel title="Run">
        <div className="text-sm text-gray-700 space-y-1">
          <div>Slot: {fmtDateTime(run.slot_ts)}</div>
          <div>Started: {fmtDateTime(run.started_at)}</div>
          <div>Finished: {run.finished_at ? fmtDateTime(run.finished_at) : '—'}</div>
          <div>
            Script:{' '}
            <Link
              to="/theses/$thesisId/scripts/$scriptId"
              params={{ thesisId, scriptId: run.script_id }}
              className="font-mono text-xs text-slate-700 hover:underline"
            >
              {run.script_id.slice(0, 8)}
            </Link>
          </div>
        </div>
      </Panel>

      {run.orders.length > 0 && (
        <Panel title={`Intents submitted (${run.orders.length})`}>
          <ul className="text-xs space-y-1 font-mono">
            {run.orders.map((o) => (
              <li key={o.id} className="flex gap-2">
                <Badge tone={o.side === 'buy' ? 'green' : 'red'}>{o.side}</Badge>
                <span>{o.contract_code}</span>
                <span className="text-text-muted">{o.size_mwh} MWh</span>
                <Badge>{o.status}</Badge>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title="stdout">
        <pre className="p-3 bg-gray-50 border border-gray-200 rounded text-[11px] whitespace-pre-wrap font-mono overflow-auto max-h-[40vh]">
          {run.stdout || '(empty)'}
        </pre>
      </Panel>
      <Panel title="stderr">
        <pre className="p-3 bg-red-50 border border-red-200 rounded text-[11px] whitespace-pre-wrap font-mono overflow-auto max-h-[40vh] text-red-800">
          {run.stderr || '(empty)'}
        </pre>
      </Panel>
    </Page>
  )
}
